import React, { Suspense } from 'react'
import { lazyWithRetry } from 'utils/lazyWithRetry'
import PropTypes from 'prop-types'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'

import { makeComponentMetaSelector } from 'selectors/navigator'
import {
  saveCTPMapping,
  resetAddRPSearch,
  setAddRpView,
} from 'actions/addReferenceParametersWorkflow'
import { CTPM_CANCEL_CTP_MAPPING } from 'actions/ctpMappingWorkflow'
import {
  getAddRPWFSelectedRPs,
  getAddRPWPStarted,
  getPinsNotAvailableModalVisible,
  getConfirmCircuitMappingModalVisible,
  getAddRPView,
  getIsAddingCollection,
} from 'selectors/addReferenceParametersWorkflow'
import { getCtpMappingModalVisible } from 'selectors/ctpMappingWorkflow'
import { classNames } from 'utils/utils'
import { Loading } from 'components/patterns'
import {
  SEARCH_TABLE_COLLECTION,
  REVIEW_COLLECTION,
  ADD_REF_PARAMETERS_WORKFLOW,
} from 'utils/constants'
import {
  removeComponentMeta,
  setSelectedIdWithDrawer,
} from 'actions/navigator.js'
import withStyle from './AddReferenceParametersWorkflow.style.js'

const AddReferenceParameters = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "AddReferenceParameters" */ 'components/AddReferenceParameters'
  )
)
const PinsNotAvailableModal = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "PinsNotAvailableModal" */ 'components/modals/PinsNotAvailableModal'
  )
)
const MapPinConfirmationModal = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "MapPinConfirmationModal" */ 'components/modals/MapPinConfirmationModal'
  )
)
const CircuitPinAssignmentModal = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "CircuitPinAssignmentModal" */ 'components/modals/CircuitPinAssignmentModal'
  )
)

const AddReferenceParametersWorkflow = ({ className, serialNumber }) => {
  const dispatch = useDispatch()

  const { conflictMessage } = useMakeSelector(makeComponentMetaSelector, {
    id: ADD_REF_PARAMETERS_WORKFLOW,
  })
  const hasStarted = useSelector(getAddRPWPStarted)
  const selectedRPs = useSelector(getAddRPWFSelectedRPs)
  const ctpModalVisible = useSelector(getCtpMappingModalVisible)
  const isAddingCollection = useSelector(getIsAddingCollection)
  const view = useSelector(getAddRPView)
  const pinsNotAvailableModalVisiable = useSelector(
    getPinsNotAvailableModalVisible
  )
  const mapPinConfirmationModalVisible = useSelector(
    getConfirmCircuitMappingModalVisible
  )

  const handleSaveOnClick = () => {
    dispatch(saveCTPMapping(selectedRPs))
    dispatch(resetAddRPSearch())

    if (isAddingCollection && view === SEARCH_TABLE_COLLECTION) {
      dispatch(setAddRpView(REVIEW_COLLECTION))
    }
  }

  const handleCancelOnClick = () => {
    dispatch({
      type: CTPM_CANCEL_CTP_MAPPING,
      flush: false,
    })
  }

  const handleCloseAddRPsToVehicle = () => {
    dispatch(removeComponentMeta([ADD_REF_PARAMETERS_WORKFLOW]))
    dispatch(setSelectedIdWithDrawer(null))
  }

  if (!hasStarted) return null

  return (
    <div className={classNames(className, 'AddReferenceParameterWorkflow')}>
      <Suspense fallback={<Loading />}>
        <AddReferenceParameters
          serialNumber={serialNumber}
          conflictMessage={conflictMessage}
          handleCloseAddRPsToVehicle={handleCloseAddRPsToVehicle}
        />
        {pinsNotAvailableModalVisiable && (
          <PinsNotAvailableModal serialNumber={serialNumber} />
        )}
        {mapPinConfirmationModalVisible && (
          <MapPinConfirmationModal serialNumber={serialNumber} />
        )}
        {ctpModalVisible && (
          <CircuitPinAssignmentModal
            serialNumber={serialNumber}
            save={handleSaveOnClick}
            visible={ctpModalVisible}
            cancel={handleCancelOnClick}
          />
        )}
      </Suspense>
    </div>
  )
}

AddReferenceParametersWorkflow.defaultProps = {}

AddReferenceParametersWorkflow.propTypes = {
  className: PropTypes.string,
  serialNumber: PropTypes.string,
}

export default withStyle(AddReferenceParametersWorkflow)
